import type { DrawRecord, GameChoice, GeneratedSet, LottoDb, LottoDbKr, MatchInfo } from '../types/lotto';

export type GameRules = {
  regularMin: number;
  regularMax: number;
  bonusMin: number;
  bonusMax: number;
  regularCount: number;
  bonusName: string;
  /** Bonus is drawn from the same drum as the regular numbers, so it can't repeat one (KR). */
  bonusDistinct: boolean;
  /** Whether first prize requires the bonus ball (US: yes; KR 6/45: no — bonus only counts for 2nd prize). */
  jackpotNeedsBonus: boolean;
};

/** Current official rules. Mega Millions: Mega Ball 1–24 since 2025-04-08. */
export const GAMES: Record<GameChoice, GameRules> = {
  us_powerball: {
    regularMin: 1,
    regularMax: 69,
    bonusMin: 1,
    bonusMax: 26,
    regularCount: 5,
    bonusName: 'Powerball',
    bonusDistinct: false,
    jackpotNeedsBonus: true,
  },
  us_megamillions: {
    regularMin: 1,
    regularMax: 70,
    bonusMin: 1,
    bonusMax: 24,
    regularCount: 5,
    bonusName: 'Mega Ball',
    bonusDistinct: false,
    jackpotNeedsBonus: true,
  },
  kr_lotto645: {
    regularMin: 1,
    regularMax: 45,
    bonusMin: 1,
    bonusMax: 45,
    regularCount: 6,
    bonusName: '보너스',
    bonusDistinct: true,
    jackpotNeedsBonus: false,
  },
};

export function isKrGame(game: GameChoice): boolean {
  return game === 'kr_lotto645';
}

export function drawsFor(game: GameChoice, dbUs: LottoDb | null, dbKr: LottoDbKr | null): DrawRecord[] | null {
  if (game === 'kr_lotto645') return dbKr?.lotto645 ?? null;
  if (!dbUs) return null;
  return game === 'us_megamillions' ? dbUs.megamillions : dbUs.powerball;
}

function randInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function generateSet(rules: GameRules): GeneratedSet {
  const numbers: number[] = [];
  while (numbers.length < rules.regularCount) {
    const n = randInt(rules.regularMin, rules.regularMax);
    if (!numbers.includes(n)) numbers.push(n);
  }
  numbers.sort((a, b) => a - b);
  let bonus: number;
  do {
    bonus = randInt(rules.bonusMin, rules.bonusMax);
  } while (rules.bonusDistinct && numbers.includes(bonus));
  return { regular: numbers, powerball: bonus };
}

export function generateSets(rules: GameRules, count = 5): GeneratedSet[] {
  return Array.from({ length: count }, () => generateSet(rules));
}

/** Past draw with the most matches (2 points per regular number, 1 for the bonus). */
export function findBestMatch(draws: DrawRecord[], set: GeneratedSet): MatchInfo | null {
  let best: MatchInfo | null = null;
  let bestScore = -1;
  for (const draw of draws) {
    const matchedNums = new Set(set.regular.filter((n) => draw.n.includes(n)));
    const bonusMatch = set.powerball === draw.b;
    const score = matchedNums.size * 2 + (bonusMatch ? 1 : 0);
    if (score > bestScore) {
      bestScore = score;
      best = {
        date: draw.d,
        numbers: [...draw.n],
        bonus: draw.b,
        regularMatches: matchedNums.size,
        bonusMatch,
        matchedNums,
        multiplier: draw.m,
        round: draw.r,
      };
    }
  }
  return best;
}

export function isJackpotMatch(rules: GameRules, match: MatchInfo | null): boolean {
  return !!match && match.regularMatches === rules.regularCount && (!rules.jackpotNeedsBonus || match.bonusMatch);
}

function jackpotKey(rules: GameRules, regular: number[], bonus: number): string {
  return rules.jackpotNeedsBonus ? `${regular.join(',')}+${bonus}` : regular.join(',');
}

/** Lookup set of every past first-prize combination; build once per game, then test sets in O(1). */
export function buildJackpotIndex(draws: DrawRecord[], rules: GameRules): Set<string> {
  return new Set(draws.map((d) => jackpotKey(rules, d.n, d.b)));
}

export function isHistoricalJackpot(index: Set<string>, rules: GameRules, set: GeneratedSet): boolean {
  return index.has(jackpotKey(rules, set.regular, set.powerball));
}

/** Demo: five sets, one of which is a real past jackpot. */
export function simulate(draws: DrawRecord[], rules: GameRules): GeneratedSet[] {
  const jackpot = draws[Math.floor(Math.random() * draws.length)]!;
  const rigged = Math.floor(Math.random() * 5);
  return Array.from({ length: 5 }, (_, i) =>
    i === rigged ? { regular: [...jackpot.n], powerball: jackpot.b } : generateSet(rules),
  );
}

export function formatMatchDate(isoDate: string, localeTag: string): string {
  return new Date(`${isoDate}T00:00:00`).toLocaleDateString(localeTag, {
    year: 'numeric',
    month: localeTag === 'en' ? 'short' : 'long',
    day: 'numeric',
  });
}
